import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Table, Thead, Tbody, Tr, Th, Td, Text, Badge,
  useToast, Spinner, Box, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { HistoricoJuzgado } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entidad: 'Detencion' | 'AudienciaJuzgado';
  entidadId: number;
  titulo: string;
}

const COLOR_ACCION: Record<string, string> = {
  CREACION: 'green',
  ACTUALIZACION: 'blue',
  CAMBIO_ESTATUS: 'orange',
  CANCELACION: 'red',
  CLASIFICACION: 'purple',
  CREACION_CITATORIO: 'teal',
  GENERACION_RESOLUCION: 'cyan',
  DESCUENTO_MANUAL: 'yellow',
  INGRESO_CUSTODIA: 'green',
  SALIDA_CUSTODIA: 'gray',
};

export default function ModalHistoricoJuzgado({ isOpen, onClose, entidad, entidadId, titulo }: Props) {
  const [historico, setHistorico] = useState<HistoricoJuzgado[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen || !entidadId) return;
    setLoading(true);
    const fn = entidad === 'Detencion'
      ? () => juzgadoApi.historicoDetencion(entidadId)
      : () => juzgadoApi.historicoAudiencia(entidadId);
    fn()
      .then(setHistorico)
      .catch(e => toast({ title: 'Error cargando historial', description: e.message, status: 'error' }))
      .finally(() => setLoading(false));
  }, [isOpen, entidadId, entidad]);

  const fmtFecha = (iso: string) => new Date(iso).toLocaleString('es-MX');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">{titulo}</ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody>
          {loading ? (
            <Box textAlign="center" py={8}><Spinner color="#5b182b" /></Box>
          ) : historico.length === 0 ? (
            <Text textAlign="center" color="gray.400" py={6}>Sin registros de historial</Text>
          ) : (
            <Accordion allowMultiple>
              {historico.map(h => (
                <AccordionItem key={h.id}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Badge colorScheme={COLOR_ACCION[h.accion] ?? 'gray'} mr={2}>{h.accion}</Badge>
                      <Text as="span" fontSize="sm">{fmtFecha(h.fecha)}</Text>
                      {h.usuarioId && <Text as="span" fontSize="xs" color="gray.500" ml={2}>— {h.usuarioId}</Text>}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel fontSize="sm">
                    {h.motivo && <Text mb={1}><strong>Motivo:</strong> {h.motivo}</Text>}
                    {h.observaciones && <Text mb={1}><strong>Obs.:</strong> {h.observaciones}</Text>}
                    {h.valoresAntJson && (
                      <Box>
                        <Text fontWeight="semibold" mb={1}>Valores anteriores:</Text>
                        <Box as="pre" bg="red.50" p={2} borderRadius="md" fontSize="xs" overflow="auto" maxH="120px">
                          {JSON.stringify(JSON.parse(h.valoresAntJson), null, 2)}
                        </Box>
                      </Box>
                    )}
                    {h.valoresNuevJson && (
                      <Box mt={2}>
                        <Text fontWeight="semibold" mb={1}>Valores nuevos:</Text>
                        <Box as="pre" bg="green.50" p={2} borderRadius="md" fontSize="xs" overflow="auto" maxH="120px">
                          {JSON.stringify(JSON.parse(h.valoresNuevJson), null, 2)}
                        </Box>
                      </Box>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
